import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApi } from "../../../service/ApiContext";
import InterviewHubApiService from "../service/InterviewHubApiService";
import {
  FeedbackResponse,
  SessionHistoryItem,
  CoreCompetenciesReport,
  CompetencyEvaluation,
} from "../data/interface/InterviewHubInterface";
import Loader from "../../../utils/Utils";
import "../styles/InterviewFeedbackStyles.css";
import RightArrow from "../../../assets/images/interviewIQ/right-arrow.png";
type FeedbackTab =
  | "overview"
  | "hr"
  | "behavioral"
  | "hiringManager"
  | "technical"
  | "competency"
  | "resume"
  | "improvements"
  | "transcript";

const InterviewFeedbackPage: React.FC = () => {
  const location = useLocation();
  const state = location.state as { sessionId?: string } | null;
  const sessionId = state?.sessionId;
  const navigate = useNavigate();
  const api = useApi();
  const service = useMemo(() => new InterviewHubApiService(api), [api]);

  const [feedback, setFeedback] = useState<FeedbackResponse | null>(null);
  const [history, setHistory] = useState<SessionHistoryItem[]>([]);
  const [activeTab, setActiveTab] = useState<FeedbackTab>("overview");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!sessionId) {
      navigate("/dashboard/interview-hub");
      return;
    }
    fetchFeedback();
  }, [sessionId, navigate]);

  const fetchFeedback = async () => {
    Loader.showLoading("Loading results...");
    try {
      const data = await service.getFeedback(sessionId!);
      setFeedback(data.feedback);
      setHistory(data.session_history || []);
    } catch (err: any) {
      console.error("Failed to fetch feedback:", err);
      setError("Failed to load results. Please try again later.");
    } finally {
      Loader.dismissLoading();
    }
  };

  const scoreColor = (score: number, max: number = 100) => {
    const pct = (score / max) * 100;
    if (pct >= 75) return "#10b981";
    if (pct >= 50) return "#f59e0b";
    return "#ef4444";
  };

  const overallPct = feedback
    ? (feedback.session_feedback.technical_readiness_score / 10) * 100
    : 0;

  const circumference = 2 * Math.PI * 52;

  const tabs: { id: FeedbackTab; label: string; icon: string }[] = [
    { id: "overview", label: "Overview", icon: "📊" },
    ...(feedback?.hr_evaluation
      ? [{ id: "hr" as FeedbackTab, label: "HR Feedback", icon: "🤝" }]
      : []),
    ...(feedback?.behavioral_evaluation
      ? [{ id: "behavioral" as FeedbackTab, label: "Behavioral", icon: "🧠" }]
      : []),
    ...(feedback?.hm_evaluation
      ? [
          {
            id: "hiringManager" as FeedbackTab,
            label: "Hiring Manager",
            icon: "👔",
          },
        ]
      : []),
    ...(feedback?.technical_skills_evaluation
      ? [
          {
            id: "technical" as FeedbackTab,
            label: "Technical Skills",
            icon: "💻",
          },
        ]
      : []),
    ...(feedback?.core_competencies_report
      ? [{ id: "competency" as FeedbackTab, label: "Competencies", icon: "🧠" }]
      : []),
    { id: "resume", label: "Resume Grade", icon: "📄" },
    { id: "improvements", label: "Improvements", icon: "💡" },
    { id: "transcript", label: "Transcript", icon: "📝" },
  ];

  const getCandidateName = () => {
    if (!feedback) return "Interview Session";
    const firstName = feedback.first_name || "";
    const lastName = feedback.last_name || "";
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return feedback.session_name || "Interview Session";
  };

  if (!feedback && !error) {
    return (
      <div className="feedback-loading-container">
        <div className="feedback-spinner" />
        <p>Loading your results…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feedback-error-container">
        <p>{error}</p>
        <button onClick={() => navigate("/dashboard/interview-hub")}>
          ← Back to Hub
        </button>
      </div>
    );
  }

  return (
    <div className="feedback-container">
      {/* Header - Modern minimal */}
      <div className="feedback-header">
        <button
          className="feedback-back-btn"
          onClick={() => navigate("/dashboard/interview-hub")}
        >
          <img src={RightArrow} alt="" />
        </button>
        <div className="feedback-header-info">
          <div className="feedback-subtitle">
            Interview Results- {feedback?.session_name || "Interview Session"}
          </div>
        </div>
      </div>

      {/* Main Layout: Sidebar + Content */}
      <div className="feedback-main-layout">
        {/* Left Sidebar - Score Card + Tabs */}
        <div className="feedback-sidebar">
          {/* Score Card inside sidebar */}
          <div className="feedback-score-card-sidebar">
            <div className="feedback-score-ring-small">
              <svg viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke={scoreColor(
                    feedback!.session_feedback.technical_readiness_score * 10,
                  )}
                  strokeWidth="8"
                  strokeDasharray={2 * Math.PI * 42}
                  strokeDashoffset={
                    2 *
                    Math.PI *
                    42 *
                    (1 -
                      feedback!.session_feedback.technical_readiness_score / 10)
                  }
                  strokeLinecap="round"
                  transform="rotate(-90 50 50)"
                  style={{ transition: "stroke-dashoffset 1s ease-out" }}
                />
              </svg>
              <div className="feedback-score-label-small">
                <div
                  className="feedback-score-number-small"
                  style={{
                    color: scoreColor(
                      feedback!.session_feedback.technical_readiness_score * 10,
                    ),
                  }}
                >
                  {feedback!.session_feedback.technical_readiness_score}
                </div>
                <div className="feedback-score-unit-small">/10</div>
              </div>
            </div>
            <div className="feedback-score-info-sidebar">
              <div className="feedback-verdict-small">
                🎯 {feedback!.session_feedback.final_verdict}
              </div>
            </div>
          </div>

          {/* Tabs */}
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`feedback-tab-btn ${activeTab === tab.id ? "feedback-tab-active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="feedback-tab-icon">{tab.icon}</span>
              <span className="feedback-tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Right Content Area */}
        <div className="feedback-content">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="feedback-content-pane">
              {/* Summary Card */}
              <div
                className="feedback-card"
                style={{ backgroundColor: "#E7EFFF" }}
              >
                <h3 className="feedback-card-title">Overall Summary</h3>
                <p className="feedback-card-text">
                  {feedback!.session_feedback.technical_readiness_justification}
                </p>
              </div>

              {/* Transcript Summary */}
              <div
                className="feedback-card"
                style={{
                  backgroundColor: "#F8FAFF",
                  border: "1px solid #CDDAF6",
                }}
              >
                <h3 className="feedback-card-title">Transcript Summary</h3>
                <p className="feedback-card-text">
                  {feedback!.session_feedback.transcript_summary}
                </p>
              </div>

              {/* Communication Style */}
              <div
                className="feedback-card"
                style={{
                  backgroundColor: "#F8FAFF",
                  border: "1px solid #CDDAF6",
                }}
              >
                <h3 className="feedback-card-title"> Communication Style</h3>
                <p className="feedback-card-text">
                  {feedback!.session_feedback.communication_style_evaluation}
                </p>
              </div>

              {/* Strengths & Improvements - DYNAMIC from feedback */}
              <div className="feedback-two-columns">
                <div className="feedback-card strengths">
                  <h3
                    className="feedback-card-title"
                    style={{ color: "#24C782" }}
                  >
                    Key Strengths
                  </h3>
                  <ul className="feedback-list">
                    {feedback!.session_feedback.key_strengths.map(
                      (strength, i) => (
                        <li key={i}>{strength}</li>
                      ),
                    )}
                  </ul>
                </div>
                <div className="feedback-card improvements">
                  <h3
                    className="feedback-card-title"
                    style={{ color: "#FF4141" }}
                  >
                    Areas for Improvement
                  </h3>
                  <ul className="feedback-list">
                    {feedback!.session_feedback.areas_for_improvement.map(
                      (area, i) => (
                        <li key={i}>{area}</li>
                      ),
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* HR Evaluation Tab */}
          {activeTab === "hr" && feedback?.hr_evaluation && (
            <div className="feedback-content-pane">
              <div className="feedback-stats-grid">
                <div className="feedback-stat-card">
                  <div className="feedback-stat-label">Motivation Score</div>
                  <div
                    className="feedback-stat-value"
                    style={{
                      color: scoreColor(
                        feedback.hr_evaluation.motivation_score * 10,
                      ),
                    }}
                  >
                    {feedback.hr_evaluation.motivation_score}
                    <span>/10</span>
                  </div>
                </div>
                <div className="feedback-stat-card">
                  <div className="feedback-stat-label">
                    Self-Awareness Score
                  </div>
                  <div
                    className="feedback-stat-value"
                    style={{
                      color: scoreColor(
                        feedback.hr_evaluation.self_awareness_score * 10,
                      ),
                    }}
                  >
                    {feedback.hr_evaluation.self_awareness_score}
                    <span>/10</span>
                  </div>
                </div>
                <div className="feedback-stat-card">
                  <div className="feedback-stat-label">Operational Fit</div>
                  <div
                    className="feedback-stat-value"
                    style={{
                      color: scoreColor(
                        feedback.hr_evaluation.operational_fit_score * 10,
                      ),
                    }}
                  >
                    {feedback.hr_evaluation.operational_fit_score}
                    <span>/10</span>
                  </div>
                </div>
              </div>
              <div className="feedback-card">
                <h3 className="feedback-card-title"> HR Narrative</h3>
                <p className="feedback-card-text">
                  {feedback.hr_evaluation.hr_narrative}
                </p>
              </div>
            </div>
          )}

          {/* Behavioral Evaluation Tab */}
          {activeTab === "behavioral" && feedback?.behavioral_evaluation && (
            <div className="feedback-content-pane">
              <div className="feedback-stats-grid">
                <div className="feedback-stat-card">
                  <div className="feedback-stat-label">Initial Reaction</div>
                  <div className="feedback-stat-value">
                    {feedback.behavioral_evaluation.initial_reaction}
                    <span>/5</span>
                  </div>
                </div>
                <div className="feedback-stat-card">
                  <div className="feedback-stat-label">
                    Stakeholder Awareness
                  </div>
                  <div className="feedback-stat-value">
                    {feedback.behavioral_evaluation.stakeholder_awareness}
                    <span>/5</span>
                  </div>
                </div>
                <div className="feedback-stat-card">
                  <div className="feedback-stat-label">Resolution Logic</div>
                  <div className="feedback-stat-value">
                    {feedback.behavioral_evaluation.resolution_logic}
                    <span>/5</span>
                  </div>
                </div>
              </div>
              <div className="feedback-card">
                <h3 className="feedback-card-title">Behavioral Tags</h3>
                <div className="feedback-tags">
                  {feedback.behavioral_evaluation.behavioral_tags.map(
                    (tag, i) => (
                      <span key={i} className="feedback-tag success">
                        {tag}
                      </span>
                    ),
                  )}
                </div>
              </div>
              {feedback.behavioral_evaluation.bias_indicators &&
                feedback.behavioral_evaluation.bias_indicators.length > 0 && (
                  <div className="feedback-card">
                    <h3 className="feedback-card-title"> Bias Indicators</h3>
                    <div className="feedback-tags">
                      {feedback.behavioral_evaluation.bias_indicators.map(
                        (bias, i) => (
                          <span key={i} className="feedback-tag warning">
                            {bias}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                )}
              <div className="feedback-card">
                <h3 className="feedback-card-title"> Justification</h3>
                <p className="feedback-card-text">
                  {feedback.behavioral_evaluation.justification}
                </p>
              </div>
            </div>
          )}

          {/* Hiring Manager Tab */}
          {activeTab === "hiringManager" && feedback?.hm_evaluation && (
            <div className="feedback-content-pane">
              <div className="feedback-stats-grid">
                <div className="feedback-stat-card">
                  <div className="feedback-stat-label">Impact Velocity</div>
                  <div className="feedback-stat-value">
                    {feedback.hm_evaluation.impact_velocity_score}
                    <span>/10</span>
                  </div>
                </div>
                <div className="feedback-stat-card">
                  <div className="feedback-stat-label">Managerial Friction</div>
                  <div className="feedback-stat-value">
                    {feedback.hm_evaluation.managerial_friction_score}
                    <span>/10</span>
                  </div>
                </div>
              </div>
              <div className="feedback-card">
                <h3 className="feedback-card-title"> Recommendation</h3>
                <p className="feedback-card-text">
                  {feedback.hm_evaluation.hm_recommendation}
                </p>
              </div>
            </div>
          )}

          {/* Technical Skills Tab */}
          {activeTab === "technical" &&
            feedback?.technical_skills_evaluation && (
              <div className="feedback-content-pane">
                <div className="feedback-stats-grid">
                  <div className="feedback-stat-card">
                    <div className="feedback-stat-label">
                      Coachability Score
                    </div>
                    <div className="feedback-stat-value">
                      {feedback.technical_skills_evaluation.coachability_score}
                      <span>/10</span>
                    </div>
                  </div>
                </div>
                <div className="feedback-card">
                  <h3 className="feedback-card-title"> Recruiter Note</h3>
                  <p className="feedback-card-text">
                    {feedback.technical_skills_evaluation.recruiter_note}
                  </p>
                </div>
                <h3 className="feedback-section-title">Skill Breakdown</h3>
                {feedback.technical_skills_evaluation.technical_scorecard.map(
                  (skill, idx) => (
                    <div key={idx} className="feedback-skill-card">
                      <div className="feedback-skill-header">
                        <span className="feedback-skill-name">
                          {skill.skill}
                        </span>
                        <span
                          className="feedback-skill-score"
                          style={{ color: scoreColor(skill.score * 10) }}
                        >
                          {skill.score}/10
                        </span>
                      </div>
                      <div className="feedback-progress-bar">
                        <div
                          className="feedback-progress-fill"
                          style={{
                            width: `${skill.score * 10}%`,
                            background: scoreColor(skill.score * 10),
                          }}
                        />
                      </div>
                      <p className="feedback-skill-scenario">
                        "{skill.initial_scenario}"
                      </p>
                      <div className="feedback-skill-meta">
                        <span>Outcome: {skill.outcome}</span>
                        <span>Hints needed: {skill.nudges_count}</span>
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}

          {/* Core Competencies Tab */}
          {activeTab === "competency" && feedback?.core_competencies_report && (
            <div className="feedback-content-pane">
              <div className="feedback-three-columns">
                <div className="feedback-card">
                  <div className="feedback-icon-large">⚡</div>
                  <h4>Your Superpower</h4>
                  <p>
                    {feedback.core_competencies_report.candidate_superpower}
                  </p>
                </div>
                <div className="feedback-card">
                  <div className="feedback-icon-large">🌱</div>
                  <h4>Growth Area</h4>
                  <p>{feedback.core_competencies_report.growth_area}</p>
                </div>
                <div className="feedback-card">
                  <div className="feedback-icon-large">💬</div>
                  <h4>Communication Tip</h4>
                  <p>{feedback.core_competencies_report.communication_tip}</p>
                </div>
              </div>

              <div className="feedback-overall-score">
                <div className="feedback-score-circle">
                  <svg viewBox="0 0 80 80">
                    <circle
                      cx="40"
                      cy="40"
                      r="34"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="6"
                    />
                    <circle
                      cx="40"
                      cy="40"
                      r="34"
                      fill="none"
                      stroke={scoreColor(
                        feedback.core_competencies_report
                          .overall_competency_score * 10,
                      )}
                      strokeWidth="6"
                      strokeDasharray={2 * Math.PI * 34}
                      strokeDashoffset={
                        2 *
                        Math.PI *
                        34 *
                        (1 -
                          feedback.core_competencies_report
                            .overall_competency_score /
                            10)
                      }
                      strokeLinecap="round"
                      transform="rotate(-90 40 40)"
                    />
                  </svg>
                  <div className="feedback-score-circle-label">
                    <span className="feedback-score-circle-number">
                      {
                        feedback.core_competencies_report
                          .overall_competency_score
                      }
                    </span>
                    <span className="feedback-score-circle-unit">/10</span>
                  </div>
                </div>
                <div>
                  <h4>Overall Competency Score</h4>
                  <p>
                    Measured across Strategic Logic, Constraint Resolution,
                    Information Discovery, and Self-Correction.
                  </p>
                </div>
              </div>

              {feedback.core_competencies_report.competency_evaluations.map(
                (ev: CompetencyEvaluation, idx: number) => (
                  <div key={idx} className="feedback-competency-card">
                    <div className="feedback-competency-header">
                      <h4>{ev.competency}</h4>
                      <span
                        className={`feedback-flag ${ev.communication_flag === "High Articulation" ? "flag-success" : "flag-warning"}`}
                      >
                        {ev.communication_flag}
                      </span>
                      <span
                        className="feedback-competency-score"
                        style={{ color: scoreColor(ev.score * 10) }}
                      >
                        {ev.score}/10
                      </span>
                    </div>
                    <div className="feedback-progress-bar">
                      <div
                        className="feedback-progress-fill"
                        style={{
                          width: `${ev.score * 10}%`,
                          background: scoreColor(ev.score * 10),
                        }}
                      />
                    </div>
                    <p className="feedback-scenario">"{ev.scenario_asked}"</p>
                    <div className="feedback-dimensions-grid">
                      <div>
                        <strong>Problem Solving</strong>
                        <br />
                        {ev.problem_solving_notes}
                      </div>
                      <div>
                        <strong>Resilience</strong>
                        <br />
                        {ev.resilience_notes}
                      </div>
                      <div>
                        <strong>Professional</strong>
                        <br />
                        {ev.professional_notes}
                      </div>
                      <div>
                        <strong>Craftsmanship</strong>
                        <br />
                        {ev.implementation_notes}
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>
          )}

          {/* Resume Grade Tab */}
          {activeTab === "resume" && feedback?.resume_score && (
            <div className="feedback-content-pane">
              {Object.entries(feedback.resume_score)
                .filter(
                  ([k]) => k !== "total" && k !== "actionable_improvements",
                )
                .map(([key, val]) => (
                  <div key={key} className="feedback-score-item">
                    <div className="feedback-score-item-header">
                      <span>
                        {key
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                      <span>{val as number}/10</span>
                    </div>
                    <div className="feedback-progress-bar">
                      <div
                        className="feedback-progress-fill"
                        style={{
                          width: `${(val as number) * 10}%`,
                          background: scoreColor((val as number) * 10),
                        }}
                      />
                    </div>
                  </div>
                ))}
              {feedback.resume_score.actionable_improvements &&
                feedback.resume_score.actionable_improvements.length > 0 && (
                  <div className="feedback-card">
                    <h3 className="feedback-card-title">
                      Actionable Improvements
                    </h3>
                    <ul className="feedback-list">
                      {feedback.resume_score.actionable_improvements.map(
                        (tip, i) => (
                          <li key={i}>{tip}</li>
                        ),
                      )}
                    </ul>
                  </div>
                )}
            </div>
          )}

          {/* Improvements Tab */}
          {activeTab === "improvements" &&
            feedback?.resume_improvement &&
            feedback.resume_improvement.length > 0 && (
              <div className="feedback-content-pane">
                {feedback.resume_improvement.map((imp, i) => (
                  <div key={i} className="feedback-improvement-card">
                    <div className="feedback-improvement-original">
                      <span className="feedback-improvement-label">
                        Original
                      </span>
                      <p>{imp.original}</p>
                    </div>
                    <div className="feedback-improvement-improved">
                      <span className="feedback-improvement-label">
                        Improved
                      </span>
                      <p>{imp.improvement}</p>
                    </div>
                    <div className="feedback-improvement-reason">
                      <span>Reason:</span> {imp.reason}
                    </div>
                  </div>
                ))}
              </div>
            )}

          {/* Transcript Tab */}
          {activeTab === "transcript" && (
            <div className="feedback-content-pane">
              {history.length > 0 ? (
                <div className="feedback-transcript">
                  {history.map((msg, i) => (
                    <div
                      key={i}
                      className={`feedback-message ${msg.role === "candidate" ? "feedback-message-user" : "feedback-message-bot"}`}
                    >
                      <div className="feedback-message-role">
                        {msg.role === "candidate" ? "You" : "JozAI"}
                      </div>
                      <div className="feedback-message-content">
                        {msg.content}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>Transcript not available.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterviewFeedbackPage;
